var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useCallback, useEffect, useMemo, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "../../components/Layout/Layout";
import { Box, Grid } from "@mui/material";
import { DashboardKpiTopHeader } from "./DashboardKpiTopHeader";
import { UserRolesPermissionsContext } from "../../../context/UserRolesPermissionsContext";
import { Modules } from "../../../application/common/enums/screen-name";
import { checkCreateEdit, checkManage, checkDelete } from "../../../application/common/utils/roles-helper";
import { KpiChartCard } from "../../components/dashboard/kpi-chart/KpiChartCard";
import { LeaderboardCard } from "../../components/dashboard/leaderboard/LeaderboardCard";
import { AdditionalLeaderboardCard } from "../../components/dashboard/leaderboard/AdditionalLeaderboardCard";
import { WeeklyTargetsModal } from "./WeeklyTargetsModal";
import { AdditionalLeaderboardFormModal } from "./AdditionalLeaderboardFormModal";
import { defaultDashboardKpiFilter, mapChartApiToStacked, mapChartApiToDay, mapLeaderboardApiToRows, } from "../../../application/pages/models/dashboard-kpi-dto";
import { mapAdditionalLeaderboardApiToRows, } from "../../../application/pages/models/additional-leaderboard-dto";
import DashboardKpiApi from "../../../infrastructure/api/pages/dashboard-kpi-api";
import AdditionalLeaderboardApi from "../../../infrastructure/api/pages/additional-leaderboard-api";
import { getYearWeekFromDate, getISOWeekStartDate, getISOWeekRange, prevYearWeek, nextYearWeek, isYearWeekBefore, isYearWeekAfter, clampDate, formatISODate, extractDateFromISO, buildWeekOptions, buildMonthOptions, buildYearOptions, } from "../../../application/common/utils/kpi-helpers";
import { useSessionFilterMemory, useSessionFilterValue, } from "../../hooks/useSessionFilterMemory";
function toApiGranularity(g) {
    return (g.charAt(0).toUpperCase() + g.slice(1));
}
// ─── Filter memory (item #2) ────────────────────────────────────────────────
//
// This screen's filter state is a date range plus a granularity and a period
// selection per card, so each value keeps its own storage entry under the
// "dashboard-kpi" screen key instead of being folded into one object — that keeps
// every existing setter call site unchanged. The stored values are only ever
// *candidates*: everything is re-clamped against the date range on mount (see the
// clamping effects below), because the range itself is restored too and a period
// outside it would otherwise render a dropdown with no matching option.
const KPI_SCREEN_KEY = "dashboard-kpi";
const isValidISODate = (value) => typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(new Date(value).getTime());
const revalidateKpiFilters = (restored) => isValidISODate(restored.startDate) &&
    isValidISODate(restored.endDate) &&
    restored.startDate <= restored.endDate
    ? restored
    : defaultDashboardKpiFilter;
const revalidateGranularity = (restored) => ["day", "week", "month", "year"].includes(restored) ? restored : "week";
const revalidateISODate = (fallback) => (restored) => isValidISODate(restored) ? restored : fallback;
const revalidateYearWeek = (fallback) => (restored) => restored &&
    Number.isInteger(restored.week) &&
    restored.week >= 1 &&
    restored.week <= 53 &&
    Number.isInteger(restored.year)
    ? restored
    : fallback;
const revalidateYearMonth = (fallback) => (restored) => restored &&
    Number.isInteger(restored.month) &&
    restored.month >= 1 &&
    restored.month <= 12 &&
    Number.isInteger(restored.year)
    ? restored
    : fallback;
const revalidateYear = (fallback) => (restored) => Number.isInteger(restored) ? restored : fallback;
const DashboardKpi = () => {
    const { t: translate } = useTranslation();
    const { RolesPermissions } = useContext(UserRolesPermissionsContext);
    const canCreate = checkCreateEdit(RolesPermissions, Modules.KPIDashboard);
    const canManage = checkManage(RolesPermissions, Modules.KPIDashboard);
    const canDelete = checkDelete(RolesPermissions, Modules.KPIDashboard);
    // Today in each shape the pickers use — computed once per mount and reused as
    // the fallback for any stored value that fails validation.
    const today = useMemo(() => {
        const now = new Date();
        return {
            iso: formatISODate(now),
            yearWeek: getYearWeekFromDate(now),
            yearMonth: { month: now.getMonth() + 1, year: now.getFullYear() },
            year: now.getFullYear(),
        };
    }, []);
    const [filters, setFilters] = useSessionFilterMemory(KPI_SCREEN_KEY, defaultDashboardKpiFilter, revalidateKpiFilters);
    const [objectsSentGranularity, setObjectsSentGranularity] = useSessionFilterValue(KPI_SCREEN_KEY, "objectsSentGranularity", "week", revalidateGranularity);
    const [objectsWonGranularity, setObjectsWonGranularity] = useSessionFilterValue(KPI_SCREEN_KEY, "objectsWonGranularity", "week", revalidateGranularity);
    const [revenueWonGranularity, setRevenueWonGranularity] = useSessionFilterValue(KPI_SCREEN_KEY, "revenueWonGranularity", "week", revalidateGranularity);
    const [leaderboardGranularity, setLeaderboardGranularity] = useSessionFilterValue(KPI_SCREEN_KEY, "leaderboardGranularity", "week", revalidateGranularity);
    const [weeklyTargetsOpen, setWeeklyTargetsOpen] = useState(false);
    const [additionalFormOpen, setAdditionalFormOpen] = useState(false);
    const [additionalGranularity, setAdditionalGranularity] = useSessionFilterValue(KPI_SCREEN_KEY, "additionalGranularity", "week", revalidateGranularity);
    const [additionalRows, setAdditionalRows] = useState([]);
    const [additionalTotalRevenue, setAdditionalTotalRevenue] = useState(0);
    const [additionalLoading, setAdditionalLoading] = useState(false);
    const [albDay, setAlbDay] = useSessionFilterValue(KPI_SCREEN_KEY, "albDay", today.iso, revalidateISODate(today.iso));
    const [albWeek, setAlbWeek] = useSessionFilterValue(KPI_SCREEN_KEY, "albWeek", today.yearWeek, revalidateYearWeek(today.yearWeek));
    const [albMonth, setAlbMonth] = useSessionFilterValue(KPI_SCREEN_KEY, "albMonth", today.yearMonth, revalidateYearMonth(today.yearMonth));
    const [albYear, setAlbYear] = useSessionFilterValue(KPI_SCREEN_KEY, "albYear", today.year, revalidateYear(today.year));
    // ─── Chart State ───────────────────────────────────────────────────────────
    const [sentDayWeek, setSentDayWeek] = useSessionFilterValue(KPI_SCREEN_KEY, "sentDayWeek", today.yearWeek, revalidateYearWeek(today.yearWeek));
    const [wonDayWeek, setWonDayWeek] = useSessionFilterValue(KPI_SCREEN_KEY, "wonDayWeek", today.yearWeek, revalidateYearWeek(today.yearWeek));
    const [revDayWeek, setRevDayWeek] = useSessionFilterValue(KPI_SCREEN_KEY, "revDayWeek", today.yearWeek, revalidateYearWeek(today.yearWeek));
    const [sentStacked, setSentStacked] = useState([]);
    const [sentDay, setSentDay] = useState([]);
    const [sentLoading, setSentLoading] = useState(false);
    const [wonStacked, setWonStacked] = useState([]);
    const [wonDay, setWonDay] = useState([]);
    const [wonLoading, setWonLoading] = useState(false);
    const [revStacked, setRevStacked] = useState([]);
    const [revDay, setRevDay] = useState([]);
    const [revLoading, setRevLoading] = useState(false);
    // ─── Leaderboard State ─────────────────────────────────────────────────────
    const [leaderboardRows, setLeaderboardRows] = useState([]);
    const [leaderboardTotalRevenue, setLeaderboardTotalRevenue] = useState(0);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    const [lbDay, setLbDay] = useSessionFilterValue(KPI_SCREEN_KEY, "lbDay", today.iso, revalidateISODate(today.iso));
    const [lbWeek, setLbWeek] = useSessionFilterValue(KPI_SCREEN_KEY, "lbWeek", today.yearWeek, revalidateYearWeek(today.yearWeek));
    const [lbMonth, setLbMonth] = useSessionFilterValue(KPI_SCREEN_KEY, "lbMonth", today.yearMonth, revalidateYearMonth(today.yearMonth));
    const [lbYear, setLbYear] = useSessionFilterValue(KPI_SCREEN_KEY, "lbYear", today.year, revalidateYear(today.year));
    // ─── Leaderboard Filter Options ────────────────────────────────────────────
    const lbWeekOptions = useMemo(() => buildWeekOptions(filters.startDate, filters.endDate), [filters.startDate, filters.endDate]);
    const lbMonthOptions = useMemo(() => buildMonthOptions(filters.startDate, filters.endDate), [filters.startDate, filters.endDate]);
    const lbYearOptions = useMemo(() => buildYearOptions(filters.startDate, filters.endDate), [filters.startDate, filters.endDate]);
    // Keeps both leaderboards' period selections inside the filter date range.
    //
    // The main leaderboard re-centres on today when the range actually *changes*
    // (unchanged behaviour), but on mount it clamps whatever is already selected
    // instead — for a fresh visit that selection is today, so the result is
    // identical, while a restored selection (item #2) survives instead of silently
    // jumping back to today. The additional leaderboard is only ever clamped: it
    // never re-centred before, and keeping it that way avoids changing behaviour
    // beyond what persistence requires.
    //
    // Every branch is idempotent and uses updater form, so a repeated run (React
    // StrictMode's double-invoked effects included) cannot clobber a restored value.
    const lbRangeRef = useRef(null);
    useEffect(() => {
        const rangeKey = `${filters.startDate}|${filters.endDate}`;
        const rangeChanged = lbRangeRef.current !== null && lbRangeRef.current !== rangeKey;
        lbRangeRef.current = rangeKey;
        const clampDay = (recentre) => (prev) => {
            const clamped = clampDate(recentre ? formatISODate(new Date()) : prev, filters.startDate, filters.endDate);
            return prev === clamped ? prev : clamped;
        };
        setLbDay(clampDay(rangeChanged));
        setAlbDay(clampDay(false));
        const minYW = lbWeekOptions.at(0);
        const maxYW = lbWeekOptions.at(-1);
        if (minYW && maxYW) {
            const clampWeek = (recentre) => (prev) => {
                const base = recentre ? getYearWeekFromDate(new Date()) : prev;
                let target;
                if (isYearWeekBefore(base, minYW))
                    target = minYW;
                else if (isYearWeekAfter(base, maxYW))
                    target = maxYW;
                else
                    target = base;
                return prev.week === target.week && prev.year === target.year ? prev : target;
            };
            setLbWeek(clampWeek(rangeChanged));
            setAlbWeek(clampWeek(false));
        }
        if (lbMonthOptions.length > 0) {
            const clampMonth = (recentre) => (prev) => {
                var _a;
                const base = recentre
                    ? { month: new Date().getMonth() + 1, year: new Date().getFullYear() }
                    : prev;
                const target = (_a = lbMonthOptions.find((o) => o.year === base.year && o.month === base.month)) !== null && _a !== void 0 ? _a : lbMonthOptions.at(-1);
                return prev.month === target.month && prev.year === target.year ? prev : target;
            };
            setLbMonth(clampMonth(rangeChanged));
            setAlbMonth(clampMonth(false));
        }
        if (lbYearOptions.length > 0) {
            const clampYear = (recentre) => (prev) => {
                const base = recentre ? new Date().getFullYear() : prev;
                const target = lbYearOptions.includes(base) ? base : lbYearOptions.at(-1);
                return prev === target ? prev : target;
            };
            setLbYear(clampYear(rangeChanged));
            setAlbYear(clampYear(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.startDate, filters.endDate, lbWeekOptions, lbMonthOptions, lbYearOptions]);
    const lbEffectiveDates = useMemo(() => {
        switch (leaderboardGranularity) {
            case "day":
                return { start: lbDay, end: lbDay };
            case "week":
                return getISOWeekRange(lbWeek.year, lbWeek.week);
            case "month": {
                const first = new Date(lbMonth.year, lbMonth.month - 1, 1);
                const last = new Date(lbMonth.year, lbMonth.month, 0);
                return { start: formatISODate(first), end: formatISODate(last) };
            }
            case "year":
                return { start: `${lbYear}-01-01`, end: `${lbYear}-12-31` };
        }
    }, [leaderboardGranularity, lbDay, lbWeek.week, lbWeek.year, lbMonth.month, lbMonth.year, lbYear]);
    // ─── Additional Leaderboard Effective Dates ───────────────────────────────
    const albEffectiveDates = useMemo(() => {
        switch (additionalGranularity) {
            case "day":
                return { start: albDay, end: albDay };
            case "week":
                return getISOWeekRange(albWeek.year, albWeek.week);
            case "month": {
                const first = new Date(albMonth.year, albMonth.month - 1, 1);
                const last = new Date(albMonth.year, albMonth.month, 0);
                return { start: formatISODate(first), end: formatISODate(last) };
            }
            case "year":
                return { start: `${albYear}-01-01`, end: `${albYear}-12-31` };
        }
    }, [additionalGranularity, albDay, albWeek.week, albWeek.year, albMonth.month, albMonth.year, albYear]);
    // ─── Day Week Navigation ──────────────────────────────────────────────────
    const filterMinYW = useMemo(() => {
        const yw = getYearWeekFromDate(new Date(filters.startDate));
        const weekStart = getISOWeekStartDate(yw.year, yw.week);
        const sd = new Date(filters.startDate);
        if (weekStart < sd)
            return nextYearWeek(yw);
        return yw;
    }, [filters.startDate]);
    const filterMaxYW = useMemo(() => getYearWeekFromDate(new Date(filters.endDate)), [filters.endDate]);
    const clampYearWeek = useCallback((yw) => {
        if (isYearWeekBefore(yw, filterMinYW))
            return filterMinYW;
        if (isYearWeekAfter(yw, filterMaxYW))
            return filterMaxYW;
        return yw;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterMinYW.week, filterMinYW.year, filterMaxYW.week, filterMaxYW.year]);
    // The three charts' day-view week navigation is clamped the same way (item #2).
    // Previously these were only clamped by the arrow handlers, which was enough
    // while they always started at today; a restored week has to be checked against
    // the range as well. clampYearWeek returns its argument untouched when already
    // in range, so an in-range selection causes no state change and no refetch.
    useEffect(() => {
        setSentDayWeek((prev) => clampYearWeek(prev));
        setWonDayWeek((prev) => clampYearWeek(prev));
        setRevDayWeek((prev) => clampYearWeek(prev));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clampYearWeek]);
    // ─── Data Fetching ─────────────────────────────────────────────────────────
    const fetchChartData = useCallback((chartType, granularity, dayWeek, setStacked, setDay, setLoading) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            const res = yield DashboardKpiApi.getChartData(filters.startDate, filters.endDate, toApiGranularity(granularity), chartType, granularity === "day" ? dayWeek.week : undefined, granularity === "day" ? dayWeek.year : undefined);
            const data = res.data;
            if (granularity === "day") {
                setDay(mapChartApiToDay(data));
                setStacked([]);
            }
            else {
                const mapped = mapChartApiToStacked(data);
                setStacked(mapped.filter((p) => extractDateFromISO(p.periodEnd) >= filters.startDate &&
                    extractDateFromISO(p.periodStart) <= filters.endDate));
                setDay([]);
            }
        }
        catch (_a) {
            setStacked([]);
            setDay([]);
        }
        finally {
            setLoading(false);
        }
    }), [filters]);
    useEffect(() => {
        fetchChartData("ObjectsSent", objectsSentGranularity, sentDayWeek, setSentStacked, setSentDay, setSentLoading);
    }, [objectsSentGranularity, sentDayWeek, fetchChartData]);
    useEffect(() => {
        fetchChartData("ObjectsWon", objectsWonGranularity, wonDayWeek, setWonStacked, setWonDay, setWonLoading);
    }, [objectsWonGranularity, wonDayWeek, fetchChartData]);
    useEffect(() => {
        fetchChartData("RevenueWon", revenueWonGranularity, revDayWeek, setRevStacked, setRevDay, setRevLoading);
    }, [revenueWonGranularity, revDayWeek, fetchChartData]);
    useEffect(() => {
        const fetchLeaderboard = () => __awaiter(void 0, void 0, void 0, function* () {
            setLeaderboardLoading(true);
            try {
                const res = yield DashboardKpiApi.getLeaderboard(lbEffectiveDates.start, lbEffectiveDates.end, toApiGranularity(leaderboardGranularity));
                const data = res.data;
                setLeaderboardRows(mapLeaderboardApiToRows(data));
                setLeaderboardTotalRevenue(data.totalRevenue);
            }
            catch (_a) {
                setLeaderboardRows([]);
                setLeaderboardTotalRevenue(0);
            }
            finally {
                setLeaderboardLoading(false);
            }
        });
        fetchLeaderboard();
    }, [leaderboardGranularity, lbEffectiveDates]);
    useEffect(() => {
        const fetchAdditionalLeaderboard = () => __awaiter(void 0, void 0, void 0, function* () {
            setAdditionalLoading(true);
            try {
                const res = yield AdditionalLeaderboardApi.getLeaderboard(albEffectiveDates.start, albEffectiveDates.end);
                const data = res.data;
                setAdditionalRows(mapAdditionalLeaderboardApiToRows(data));
                setAdditionalTotalRevenue(data.totaleTotaleInkomsten);
            }
            catch (_a) {
                setAdditionalRows([]);
                setAdditionalTotalRevenue(0);
            }
            finally {
                setAdditionalLoading(false);
            }
        });
        fetchAdditionalLeaderboard();
    }, [additionalGranularity, albEffectiveDates]);
    // ─── Weekly Targets ────────────────────────────────────────────────────────
    const handleSetWeeklyTargets = useCallback(() => setWeeklyTargetsOpen(true), []);
    const handleCloseWeeklyTargets = () => {
        setWeeklyTargetsOpen(false);
        fetchChartData("ObjectsSent", objectsSentGranularity, sentDayWeek, setSentStacked, setSentDay, setSentLoading);
        fetchChartData("ObjectsWon", objectsWonGranularity, wonDayWeek, setWonStacked, setWonDay, setWonLoading);
        fetchChartData("RevenueWon", revenueWonGranularity, revDayWeek, setRevStacked, setRevDay, setRevLoading);
    };
    // ─── Render ────────────────────────────────────────────────────────────────
    return (_jsxs(Layout, { children: [_jsx(DashboardKpiTopHeader, { filters: filters, onFiltersChange: setFilters, onSetWeeklyTargets: canCreate ? handleSetWeeklyTargets : undefined }), _jsxs(Grid, { container: true, spacing: { lg: 3, md: 2, xs: 1.5 }, sx: { width: "100%" }, children: [_jsx(Grid, { size: {
                            lg: 6,
                            md: 12,
                            xs: 12
                        }, children: _jsxs(Box, { sx: {
                                maxHeight: { xs: "auto", md: "calc(100dvh - 195px)" },
                                overflowY: "auto",
                                flexDirection: "column",
                                borderRadius: 2,
                                display: "flex",
                                gap: 2,
                                "&::-webkit-scrollbar": { display: "none" },
                                msOverflowStyle: "none",
                                scrollbarWidth: "none",
                            }, children: [_jsx(KpiChartCard, { title: translate("ObjectsSentInQuotations"), yAxisLabel: translate("numberOfObjectsSent"), chartType: "ObjectsSent", granularity: objectsSentGranularity, onGranularityChange: setObjectsSentGranularity, stackedData: sentStacked, dayData: sentDay, loading: sentLoading, showNavArrows: true, dayWeek: sentDayWeek.week, dayYear: sentDayWeek.year, onPrevPeriod: () => setSentDayWeek((w) => clampYearWeek(prevYearWeek(w))), onNextPeriod: () => setSentDayWeek((w) => clampYearWeek(nextYearWeek(w))), filterStartDate: filters.startDate, filterEndDate: filters.endDate }), _jsx(KpiChartCard, { title: translate("ObjectsWonInQuotations"), yAxisLabel: translate("numberOfObjectsWon"), chartType: "ObjectsWon", granularity: objectsWonGranularity, onGranularityChange: setObjectsWonGranularity, stackedData: wonStacked, dayData: wonDay, loading: wonLoading, showNavArrows: true, dayWeek: wonDayWeek.week, dayYear: wonDayWeek.year, onPrevPeriod: () => setWonDayWeek((w) => clampYearWeek(prevYearWeek(w))), onNextPeriod: () => setWonDayWeek((w) => clampYearWeek(nextYearWeek(w))), filterStartDate: filters.startDate, filterEndDate: filters.endDate }), _jsx(KpiChartCard, { title: translate("RevenueWonChart"), yAxisLabel: translate("Revenue"), chartType: "RevenueWon", granularity: revenueWonGranularity, onGranularityChange: setRevenueWonGranularity, stackedData: revStacked, dayData: revDay, loading: revLoading, showNavArrows: true, dayWeek: revDayWeek.week, dayYear: revDayWeek.year, onPrevPeriod: () => setRevDayWeek((w) => clampYearWeek(prevYearWeek(w))), onNextPeriod: () => setRevDayWeek((w) => clampYearWeek(nextYearWeek(w))), filterStartDate: filters.startDate, filterEndDate: filters.endDate })] }) }), _jsx(Grid, { size: {
                            lg: 6,
                            md: 12,
                            xs: 12
                        }, children: _jsxs(Box, { sx: {
                                maxHeight: { xs: "auto", md: "calc(100dvh - 195px)" },
                                overflowY: "auto",
                                flexDirection: "column",
                                borderRadius: 2,
                                display: "flex",
                                gap: 2,
                                "&::-webkit-scrollbar": { display: "none" },
                                msOverflowStyle: "none",
                                scrollbarWidth: "none",
                            }, children: [_jsx(LeaderboardCard, { granularity: leaderboardGranularity, onGranularityChange: setLeaderboardGranularity, rows: leaderboardRows, totalRevenue: leaderboardTotalRevenue, loading: leaderboardLoading, filters: filters, effectiveDates: lbEffectiveDates, lbDay: lbDay, onLbDayChange: setLbDay, lbWeek: lbWeek, onLbWeekChange: setLbWeek, lbWeekOptions: lbWeekOptions, lbMonth: lbMonth, onLbMonthChange: setLbMonth, lbMonthOptions: lbMonthOptions, lbYear: lbYear, onLbYearChange: setLbYear, lbYearOptions: lbYearOptions }), _jsx(AdditionalLeaderboardCard, { granularity: additionalGranularity, onGranularityChange: setAdditionalGranularity, rows: additionalRows, totaleTotaleInkomsten: additionalTotalRevenue, loading: additionalLoading, filters: filters, lbDay: albDay, onLbDayChange: setAlbDay, lbWeek: albWeek, onLbWeekChange: setAlbWeek, lbWeekOptions: lbWeekOptions, lbMonth: albMonth, onLbMonthChange: setAlbMonth, lbMonthOptions: lbMonthOptions, lbYear: albYear, onLbYearChange: setAlbYear, lbYearOptions: lbYearOptions, canCreate: canCreate, onFillInOutcome: () => setAdditionalFormOpen(true) })] }) })] }), _jsx(WeeklyTargetsModal, { open: weeklyTargetsOpen, onClose: handleCloseWeeklyTargets, canCreate: canCreate, canManage: canManage, canDelete: canDelete }), _jsx(AdditionalLeaderboardFormModal, { open: additionalFormOpen, onClose: () => {
                    setAdditionalFormOpen(false);
                    // refresh table after managing entries
                    AdditionalLeaderboardApi.getLeaderboard(albEffectiveDates.start, albEffectiveDates.end)
                        .then((res) => {
                        const data = res.data;
                        setAdditionalRows(mapAdditionalLeaderboardApiToRows(data));
                        setAdditionalTotalRevenue(data.totaleTotaleInkomsten);
                    })
                        .catch(() => { });
                }, canCreate: canCreate, canManage: canManage, canDelete: canDelete })] }));
};
export default DashboardKpi;
